import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";

const Navbar = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "services", label: t.nav.services },
    { href: "projects", label: t.nav.projects },
    { href: "about", label: t.nav.about },
    { href: "contact", label: t.nav.contact },
  ];

  const handleAnchor = (id) => {
    setOpen(false);
    if (onHome) {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#121212]/85 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href}`}
                onClick={() => handleAnchor(l.href)}
                className="font-mono text-[11px] text-white/60 hover:text-white transition-colors duration-300 tracking-[0.25em]"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              data-testid="language-toggle"
              className="font-mono text-[11px] border border-white/20 hover:border-white text-white/80 hover:text-white transition-colors px-3 py-2 tracking-[0.25em]"
              aria-label="Toggle language"
            >
              <span className={lang === "de" ? "text-white" : "text-white/40"}>
                DE
              </span>
              <span className="mx-1 text-white/30">/</span>
              <span className={lang === "en" ? "text-white" : "text-white/40"}>
                EN
              </span>
            </button>

            <button
              onClick={() => handleAnchor("contact")}
              data-testid="nav-cta"
              className="hidden md:inline-flex bg-[#FF3B30] hover:bg-[#D63026] text-white font-mono text-[11px] tracking-[0.25em] px-5 py-2.5 transition-colors duration-300"
            >
              {t.nav.cta}
            </button>

            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-white p-2"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#121212] border-t border-white/10"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => handleAnchor(l.href)}
                data-testid={`mobile-nav-${l.href}`}
                className="font-display text-3xl text-white text-left hover:text-[#FF3B30] transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => handleAnchor("contact")}
              data-testid="mobile-nav-cta"
              className="mt-4 bg-[#FF3B30] hover:bg-[#D63026] text-white font-mono text-xs tracking-[0.25em] px-6 py-4 self-start"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
